import { Check } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { PrimaryButton, SecondaryButton } from "@/components/StandardButton";
import { useNavigate, Link } from "react-router-dom";
import { SectionHeader, BodyText } from "@/components/ui/typography";
import { Badge } from "@/components/ui/badge";

const plans = [
  {
    name: "Starter",
    price: 39,
    description: "For small teams getting started",
    features: [
      "100 submissions/month",
      "2 feedback pages",
      "Basic AI sentiment",
      "Email support",
    ],
    popular: false,
  },
  {
    name: "Professional",
    price: 79,
    description: "For growing teams",
    features: [
      "Unlimited submissions",
      "10 feedback pages",
      "Advanced AI insights",
      "HR response templates",
      "Priority support",
    ],
    popular: true,
  },
  {
    name: "Business",
    price: 149,
    description: "For organizations at scale",
    features: [
      "Unlimited everything",
      "White-label branding",
      "Full API access",
      "Custom integrations",
      "4-hour support SLA",
    ],
    popular: false,
  },
];

export const PricingPreview = () => {
  const navigate = useNavigate();

  return (
    <section className="container mx-auto px-4 md:px-6 lg:px-8 py-12 md:py-16">
      <div className="max-w-6xl mx-auto space-y-8 md:space-y-12">
        <div className="text-center space-y-2 max-w-3xl mx-auto">
          <SectionHeader className="text-foreground">
            Simple, Transparent Pricing
          </SectionHeader>
          <BodyText className="text-muted-foreground">
            Start free for 15 days. No credit card required.
          </BodyText>
        </div>

        <div className="grid md:grid-cols-3 gap-6 md:gap-8">
          {plans.map((plan) => (
            <Card
              key={plan.name}
              className={`relative border bg-card p-6 md:p-8 flex flex-col ${
                plan.popular
                  ? "border-primary shadow-lg ring-2 ring-primary/20"
                  : "border-border"
              }`}
            >
              {plan.popular && (
                <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground">
                  Most Popular
                </Badge>
              )}

              <div className="space-y-4 flex-1">
                <div>
                  <h3 className="text-xl font-semibold text-foreground">
                    {plan.name}
                  </h3>
                  <BodyText variant="secondary" className="mt-1">
                    {plan.description}
                  </BodyText>
                </div>

                <div className="flex items-baseline gap-1">
                  <span className="text-4xl font-bold text-foreground">
                    ${plan.price}
                  </span>
                  <span className="text-muted-foreground">/month</span>
                </div>

                <ul className="space-y-3">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2">
                      <Check className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-muted-foreground">
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-6 pt-6 border-t border-border">
                {plan.popular ? (
                  <PrimaryButton
                    className="w-full"
                    onClick={() => navigate("/auth?mode=signup")}
                  >
                    Start Free Trial
                  </PrimaryButton>
                ) : (
                  <SecondaryButton
                    className="w-full"
                    onClick={() => navigate("/auth?mode=signup")}
                  >
                    Start Free Trial
                  </SecondaryButton>
                )}
              </div>
            </Card>
          ))}
        </div>

        <div className="text-center">
          <Link
            to="/pricing"
            className="text-sm text-primary hover:underline font-medium"
          >
            View full feature comparison →
          </Link>
        </div>
      </div>
    </section>
  );
};
